import axios from 'axios'
import {
  apiURL,
  commonHeadersWeb,
  getCountryCode,
  getMainLanguageCode,
} from 'src/consumer/services'
import { platformWeb } from 'src/consumer/services'

const SUCCESS_CODE = 2000

/**
 * Base path for rx-links: nc (no auth) or secure (Bearer token).
 * @param {string} linkToken
 * @param {boolean} isSecure - use secure APIs (requires auth)
 */
function getRxLinksBase(linkToken, isSecure) {
  const prefix = isSecure ? '/api/secure/rx-links' : '/api/nc/rx-links'
  return `${apiURL}${prefix}/${encodeURIComponent(linkToken)}`
}

function getHeaders(isSecure) {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null
  return {
    ...commonHeadersWeb,
    'ef-user-country-code': getCountryCode(),
    'ef-app-language': getMainLanguageCode(),
    ...(isSecure && token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

function getParams() {
  return {
    languageCode: getMainLanguageCode(),
    platform: platformWeb,
    countryCode: getCountryCode(),
  }
}

/**
 * GET link metadata (clinic, doctor, masked patient, dates, opens).
 * @param {string} linkToken
 * @param {boolean} isSecure
 * @returns {Promise<{ data?: object, code?: number, response?: object }>}
 */
export async function getRxLinkMeta(linkToken, isSecure = false) {
  const url = getRxLinksBase(linkToken, isSecure)
  try {
    const res = await axios.get(url, {
      headers: getHeaders(isSecure),
      params: getParams(),
    })
    return res?.data?.code === SUCCESS_CODE ? { data: res.data.data, code: res.data.code } : { code: res?.data?.code, message: res?.data?.message }
  } catch (err) {
    return {
      response: err.response,
      code: err.response?.data?.code,
      status: err.response?.status,
      message: err.response?.data?.message || err.message,
    }
  }
}

/**
 * GET consent info (declaration text, actor types, verification policy).
 * @param {string} linkToken
 * @param {boolean} isSecure
 */
export async function getRxLinkConsentInfo(linkToken, isSecure = false) {
  const url = `${getRxLinksBase(linkToken, isSecure)}/consent-info`
  try {
    const res = await axios.get(url, {
      headers: getHeaders(isSecure),
      params: getParams(),
    })
    return res?.data?.code === SUCCESS_CODE ? { data: res.data.data, code: res.data.code } : { code: res?.data?.code, message: res?.data?.message }
  } catch (err) {
    return {
      response: err.response,
      code: err.response?.data?.code,
      status: err.response?.status,
      message: err.response?.data?.message || err.message,
    }
  }
}

/**
 * POST start contact verification (OTP).
 * @param {string} linkToken
 * @param {{ contactType: string, contactValue: string, channel: string }} body
 * @param {boolean} isSecure
 */
export async function startContactVerification(linkToken, body, isSecure = false) {
  const url = `${getRxLinksBase(linkToken, isSecure)}/contact-verification/start`
  try {
    const res = await axios.post(url, body, {
      headers: { ...getHeaders(isSecure), 'Content-Type': 'application/json' },
      params: getParams(),
    })
    return res?.data?.code === SUCCESS_CODE ? { data: res.data.data, code: res.data.code } : { code: res?.data?.code, message: res?.data?.message }
  } catch (err) {
    return {
      response: err.response,
      code: err.response?.data?.code,
      status: err.response?.status,
      message: err.response?.data?.message || err.message,
    }
  }
}

/**
 * POST verify OTP.
 * @param {string} linkToken
 * @param {{ sessionId: string, otp: string }} body
 * @param {boolean} isSecure
 */
export async function verifyContactOtp(linkToken, body, isSecure = false) {
  const url = `${getRxLinksBase(linkToken, isSecure)}/contact-verification/verify`
  try {
    const res = await axios.post(url, body, {
      headers: { ...getHeaders(isSecure), 'Content-Type': 'application/json' },
      params: getParams(),
    })
    return res?.data?.code === SUCCESS_CODE ? { data: res.data.data, code: res.data.code } : { code: res?.data?.code, message: res?.data?.message }
  } catch (err) {
    return {
      response: err.response,
      code: err.response?.data?.code,
      status: err.response?.status,
      message: err.response?.data?.message || err.message,
    }
  }
}

/**
 * POST submit consent (get view token).
 * Body: { accepted, actorType, verificationSessionId } or secure: { accepted, actorType, contactType?, contactValue? }
 * @param {string} linkToken
 * @param {object} body
 * @param {boolean} isSecure
 */
export async function submitConsent(linkToken, body, isSecure = false) {
  const url = `${getRxLinksBase(linkToken, isSecure)}/consent`
  try {
    const res = await axios.post(url, body, {
      headers: { ...getHeaders(isSecure), 'Content-Type': 'application/json' },
      params: getParams(),
    })
    const data = res?.data
    const payload = data?.data || data
    // API may return { rxId, viewToken, viewTokenExpiresAt } at top level (no code wrapper)
    if (payload?.rxId && payload?.viewToken) {
      return {
        rxId: payload.rxId,
        viewToken: payload.viewToken,
        viewTokenExpiresAt: payload.viewTokenExpiresAt,
        code: data?.code ?? SUCCESS_CODE,
      }
    }
    return { code: data?.code, message: data?.message || payload?.message }
  } catch (err) {
    return {
      response: err.response,
      code: err.response?.data?.code,
      status: err.response?.status,
      message: err.response?.data?.message || err.message,
    }
  }
}

/**
 * POST request more access (reissue link). Use when open limit (409) or meta shows opens exhausted.
 * Sends a fresh link to the patient (WhatsApp/email). Only works if consent already exists.
 * @param {string} linkToken
 * @param {boolean} isSecure
 * @returns {Promise<{ sent?: boolean, code?: number, message?: string }>}
 */
export async function requestMoreAccess(linkToken, isSecure = false) {
  const url = `${getRxLinksBase(linkToken, isSecure)}/request-more-access`
  try {
    const res = await axios.post(url, {}, {
      headers: getHeaders(isSecure),
      params: getParams(),
    })
    const data = res?.data
    const payload = data?.data || data
    if (res?.data?.code === SUCCESS_CODE && payload?.sent === true) {
      return { sent: true, code: data.code }
    }
    return { code: data?.code, message: data?.message || payload?.message }
  } catch (err) {
    return {
      response: err.response,
      code: err.response?.data?.code,
      status: err.response?.status,
      message: err.response?.data?.message || err.message,
    }
  }
}

/**
 * Fetch prescription PDF as Blob. Use view token in query to avoid consuming token on range requests.
 * GET /api/nc/prescriptions/{rxId}/file?vt={viewToken}
 * @param {string} rxId
 * @param {string} viewToken
 * @returns {Promise<Blob>}
 */
export async function fetchPrescriptionPdf(rxId, viewToken) {
  const url = `${apiURL}/api/nc/prescriptions/${encodeURIComponent(rxId)}/file`
  const res = await axios.get(url, {
    params: { vt: viewToken, ...getParams() },
    headers: getHeaders(false),
    responseType: 'blob',
  })
  return res.data
}
